import { RowOpenButton } from "@/components/app/row-actions";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { createFileRoute, redirect, useBlocker } from "@tanstack/react-router";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Factory,
  MailPlus,
  Power,
  UserMinus,
  UserPlus,
  Users as UsersIcon,
  X,
} from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { normalizeMobile } from "@nexseed/shared";
import { ConfirmDialog } from "@/components/app/dialogs";
import { EmptyState } from "@/components/app/empty-state";
import { FilterBar } from "@/components/app/filter-bar";
import { PageHeader } from "@/components/app/page-header";
import { SearchField } from "@/components/app/search-field";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Callout } from "@/components/ui/callout";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Select } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  accessWindowOf,
  accessWindowPayload,
} from "@/features/admin/access-window";
import {
  adminApi,
  adminKeys,
  STATUS_LABEL,
  type AdminRole,
  type AdminUser,
} from "@/features/admin/api";
import {
  AccessMatrix,
  MatrixLegend,
  RoleColumnHead,
  type MatrixCell,
  type MatrixCol,
  type MatrixRow,
} from "@/features/admin/components/access-matrix";
import {
  ChangesControls,
  FloatingBar,
  ReviewDialog,
} from "@/features/admin/components/changes-bar";
import { InviteSheet } from "@/features/admin/components/invite-sheet";
import {
  Avatar,
  PlantCodes,
  RoleDeviceMark,
  UserStatusBadge,
} from "@/features/admin/components/person";
import { PersonSheet } from "@/features/admin/components/person-sheet";
import { useStagedGrid, type GridBase } from "@/features/admin/staged-grid";
import { useAuth } from "@/features/auth/store";
import { ApiError } from "@/lib/api-client";
import { cn } from "@/lib/utils";

const search = z.object({
  status: z
    .enum(["invited", "active", "inactive", "suspended"])
    .default("active"),
  q: z.string().optional(),
  roleId: z.number().int().positive().optional(),
  facilityId: z.number().int().positive().optional(),
  source: z.enum(["new", "core"]).optional(),
  /** The access window, which is orthogonal to status: an expired user is still 'active'. */
  access: z.enum(["scheduled", "active", "expired"]).optional(),
  page: z.number().int().min(1).default(1),
});

export const Route = createFileRoute("/_app/admin/users")({
  validateSearch: search,
  // users.manage, not a role name: whoever is granted it onboards people, whatever their role.
  beforeLoad: () => {
    if (!useAuth.getState().can("users.manage")) throw redirect({ to: "/" });
  },
  component: Users,
});

/** Count-pill colour per status tab. */
const TAB_TONE = {
  invited: "warning",
  active: "good",
  inactive: "neutral",
  suspended: "danger",
} as const;

const SELF_LOCK = "Your own roles are changed by another admin";

const errText = (e: unknown, fallback: string) =>
  e instanceof ApiError ? e.message : fallback;

const plural = (n: number, one: string, many: string) =>
  `${n} ${n === 1 ? one : many}`;

function Users() {
  const qc = useQueryClient();
  const navigate = Route.useNavigate();
  const s = Route.useSearch();
  const selfId = useAuth((a) => a.me?.id ?? null);
  const [invite, setInvite] = useState(false);
  const [openId, setOpenId] = useState<number | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [review, setReview] = useState(false);
  const [bulk, setBulk] = useState<
    { kind: "deactivate" } | { kind: "plant"; id: number; label: string } | null
  >(null);

  const onSearch = (next: Partial<typeof s>) =>
    void navigate({ search: (prev) => ({ ...prev, ...next }), replace: true });

  const counts = useQuery({
    queryKey: adminKeys.tabCounts,
    queryFn: adminApi.tabCounts,
  });
  const query = {
    status: s.status,
    q: s.q,
    roleId: s.roleId,
    facilityId: s.facilityId,
    source: s.source,
    access: s.access,
    page: s.page,
    limit: 50,
  };
  const list = useQuery({
    queryKey: adminKeys.users(query),
    queryFn: () => adminApi.users(query),
    placeholderData: keepPreviousData,
  });
  // Roles are the grid's columns and the invite drawer's checkboxes. Someone who may onboard but
  // not edit roles still needs to read the list, so this is not gated on roles.manage.
  const roles = useQuery({
    queryKey: adminKeys.roles,
    queryFn: adminApi.roles,
  });
  const facilities = useQuery({
    queryKey: adminKeys.facilities,
    queryFn: adminApi.facilities,
    staleTime: 5 * 60_000,
    select: (d) => d.items,
  });

  const items = useMemo(() => list.data?.items ?? [], [list.data]);
  const roleList = useMemo(() => roles.data?.items ?? [], [roles.data]);
  const roleName = useMemo(
    () => new Map(roleList.map((r) => [String(r.id), r.name])),
    [roleList]
  );

  // Everyone seen on any page this visit. Staged edits outlive paging and filtering, so the grid's
  // base must still know what an off-screen person holds, or a staged change for them would vanish.
  const [known, setKnown] = useState<ReadonlyMap<number, AdminUser>>(new Map());
  const [merged, setMerged] = useState<AdminUser[] | null>(null);
  if (merged !== items) {
    setMerged(items);
    const next = new Map(known);
    for (const u of items) next.set(u.id, u);
    setKnown(next);
  }
  const base: GridBase = useMemo(
    () =>
      new Map(
        [...known.values()].map((u) => [
          String(u.id),
          new Set(u.roles.map((r) => String(r.id))),
        ])
      ),
    [known]
  );
  const grid = useStagedGrid(base);

  useBlocker({
    shouldBlockFn: ({ next }) =>
      next.pathname !== "/admin/users" &&
      grid.dirty &&
      !window.confirm("Leave without saving the role changes?"),
    enableBeforeUnload: () => grid.dirty,
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ["admin"] });

  const save = useMutation({
    mutationFn: () =>
      adminApi.setUserRoles(
        grid.diff.map((d) => ({
          userId: Number(d.row),
          add: d.add.map(Number),
          remove: d.remove.map(Number),
        }))
      ),
    onSuccess: async () => {
      toast.success(
        `Roles saved for ${plural(grid.diff.length, "person", "people")}. They re-authenticate on their next request.`
      );
      setReview(false);
      // Clear the staged marks only once the saved roles are back, so no cell flashes its old value.
      await refresh();
      grid.discard();
    },
  });

  /** Runs one call per selected person, then reports how many went through. */
  const runBulk = async (
    label: string,
    fn: (u: AdminUser) => Promise<unknown>
  ) => {
    const targets = [...selected]
      .map((id) => known.get(id))
      .filter((u): u is AdminUser => !!u && u.id !== selfId);
    const results = await Promise.allSettled(targets.map(fn));
    const failed = results.filter((r) => r.status === "rejected");
    if (failed.length)
      toast.error(
        `${label}: ${targets.length - failed.length} of ${targets.length} done. ${errText((failed[0] as PromiseRejectedResult).reason, "")}`
      );
    else
      toast.success(`${label}: ${plural(targets.length, "person", "people")}`);
    setBulk(null);
    setSelected(new Set());
    void refresh();
  };

  const selectable = items.filter((u) => u.id !== selfId);
  const allOnPage =
    selectable.length > 0 && selectable.every((u) => selected.has(u.id));
  const someOnPage = selectable.some((u) => selected.has(u.id));
  const toggleSelect = (id: number, on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });

  const cols: MatrixCol[] = roleList.map((r) => ({
    key: String(r.id),
    label: r.name,
    header: (
      <RoleColumnHead
        name={r.name}
        meta={
          <>
            <UsersIcon className="h-3 w-3" aria-hidden />
            <span className="num">{r.userCount}</span>
            <RoleDeviceMark role={r} />
          </>
        }
      />
    ),
  }));

  const rows: MatrixRow[] = items.map((u) => ({
    key: String(u.id),
    label: u.fullName,
    locked: u.id === selfId ? SELF_LOCK : null,
    header: (
      <PersonHead
        user={u}
        isSelf={u.id === selfId}
        selected={selected.has(u.id)}
        onSelect={(on) => toggleSelect(u.id, on)}
        onOpen={() => setOpenId(u.id)}
      />
    ),
  }));

  const cell = (row: MatrixRow, col: MatrixCol): MatrixCell => ({
    kind: "leaf",
    state: grid.state(row.key, col.key),
  });
  const cellLabel = (row: MatrixRow, col: MatrixCol, c: MatrixCell) => {
    const st = c.kind === "leaf" ? c.state : "none";
    if (st === "held") return `Remove ${col.label} from ${row.label}`;
    if (st === "add") return `Undo granting ${col.label} to ${row.label}`;
    if (st === "remove") return `Undo removing ${col.label} from ${row.label}`;
    return `Grant ${col.label} to ${row.label}`;
  };

  const stageForSelection = (roleId: number, on: boolean) =>
    grid.set(
      [...selected]
        .filter((id) => id !== selfId)
        .map((id) => [String(id), String(roleId)] as const),
      on
    );

  const openUser = openId !== null ? (known.get(openId) ?? null) : null;
  const filtersActive =
    (s.q ? 1 : 0) +
    (s.roleId ? 1 : 0) +
    (s.facilityId ? 1 : 0) +
    (s.source ? 1 : 0) +
    (s.access ? 1 : 0);

  return (
    <>
      <PageHeader
        title="Users"
        description="Who can sign in, which roles they hold, and which plants they work at."
        actions={
          <Button variant="accent" onClick={() => setInvite(true)}>
            <MailPlus className="h-4 w-4" /> Invite user
          </Button>
        }
      />

      <Tabs
        value={s.status}
        onValueChange={(v) => {
          setSelected(new Set());
          onSearch({ status: v as typeof s.status, page: 1 });
        }}
      >
        <TabsList>
          {(["invited", "active", "inactive", "suspended"] as const).map(
            (k) => (
              <TabsTrigger
                key={k}
                value={k}
                tone={TAB_TONE[k]}
                count={counts.data ? (counts.data[k] ?? 0) : undefined}
              >
                {STATUS_LABEL[k]}
              </TabsTrigger>
            )
          )}
        </TabsList>
      </Tabs>

      <FilterBar
        className="mb-3"
        activeCount={filtersActive}
        onReset={() =>
          onSearch({
            q: undefined,
            roleId: undefined,
            facilityId: undefined,
            source: undefined,
            access: undefined,
            page: 1,
          })
        }
      >
        <SearchField
          value={s.q ?? ""}
          onChange={(q) => onSearch({ q, page: 1 })}
          placeholder="Name, e-mail or employee code"
        />
        <Select
          aria-label="Role"
          value={s.roleId ? String(s.roleId) : ""}
          onChange={(e) =>
            onSearch({
              roleId: e.target.value ? Number(e.target.value) : undefined,
              page: 1,
            })
          }
        >
          <option value="">All roles</option>
          {roleList.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
            </option>
          ))}
        </Select>
        <Select
          aria-label="Plant"
          value={s.facilityId ? String(s.facilityId) : ""}
          onChange={(e) =>
            onSearch({
              facilityId: e.target.value ? Number(e.target.value) : undefined,
              page: 1,
            })
          }
        >
          <option value="">All plants</option>
          {(facilities.data ?? []).map((f) => (
            <option key={f.id} value={f.id}>
              {f.plantCode} · {f.name}
            </option>
          ))}
        </Select>
        <Select
          aria-label="Onboarding"
          value={s.source ?? ""}
          onChange={(e) =>
            onSearch({
              source: (e.target.value || undefined) as
                "new" | "core" | undefined,
              page: 1,
            })
          }
        >
          <option value="">Any onboarding</option>
          <option value="core">From HR directory</option>
          <option value="new">Entered manually</option>
        </Select>
        <Select
          aria-label="Access window"
          value={s.access ?? ""}
          onChange={(e) =>
            onSearch({
              access: (e.target.value || undefined) as
                "scheduled" | "active" | "expired" | undefined,
              page: 1,
            })
          }
        >
          <option value="">Any access</option>
          <option value="active">Active now</option>
          <option value="scheduled">Scheduled</option>
          <option value="expired">Expired</option>
        </Select>
      </FilterBar>

      <section
        aria-busy={list.isFetching || undefined}
        className="relative overflow-hidden rounded-2xl border border-border bg-surface-raised shadow-card"
      >
        {list.isFetching && !list.isLoading && (
          <div
            className="absolute inset-x-0 top-0 z-40 h-0.5 overflow-hidden"
            aria-hidden
          >
            <div className="h-full w-1/3 bg-brand motion-safe:animate-sweep" />
          </div>
        )}

        {list.error ? (
          <div className="p-4">
            <Callout
              tone="danger"
              title="The list could not be loaded"
              action={
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => void list.refetch()}
                >
                  Retry
                </Button>
              }
            >
              {errText(list.error, "Try again in a moment.")}
            </Callout>
          </div>
        ) : list.isLoading || roles.isLoading ? (
          <GridSkeleton />
        ) : items.length === 0 ? (
          <EmptyState
            icon={filtersActive ? UsersIcon : MailPlus}
            title={
              filtersActive
                ? "Nobody matches these filters."
                : `No ${STATUS_LABEL[s.status].toLowerCase()} users.`
            }
            description={
              filtersActive
                ? "Clear a filter, or look under another status tab."
                : s.status === "invited"
                  ? "Invitations you send wait here until the person sets a password."
                  : undefined
            }
            action={
              filtersActive ? undefined : (
                <Button size="sm" onClick={() => setInvite(true)}>
                  <UserPlus className="h-4 w-4" /> Invite user
                </Button>
              )
            }
          />
        ) : (
          <>
            <AccessMatrix
              label="Roles held by each person"
              className={cn(
                "hidden min-h-64 md:block",
                // Leave room for the floating bar so it never sits on the last rows or the pager.
                selected.size > 0 || grid.dirty
                  ? "max-h-[calc(100dvh-23rem)]"
                  : "max-h-[calc(100dvh-19rem)]"
              )}
              corner={
                <div className="flex items-center gap-2.5">
                  <Checkbox
                    aria-label="Select everyone on this page"
                    checked={
                      allOnPage ? true : someOnPage ? "indeterminate" : false
                    }
                    onCheckedChange={(c) =>
                      setSelected((prev) => {
                        const next = new Set(prev);
                        for (const u of selectable)
                          if (c === true) next.add(u.id);
                          else next.delete(u.id);
                        return next;
                      })
                    }
                  />
                  <span className="text-xs font-bold uppercase tracking-wide text-ink-muted">
                    Person
                  </span>
                </div>
              }
              rows={rows}
              cols={cols}
              cell={cell}
              cellLabel={cellLabel}
              onToggle={(row, col) => grid.toggle(row.key, col.key)}
              rowHeaderClass="w-72 min-w-72"
            />
            <PeopleList users={items} onOpen={(u) => setOpenId(u.id)} />
          </>
        )}

        <footer className="flex flex-wrap items-center justify-between gap-3 border-t border-border bg-surface-sunken/60 px-4 py-2.5 text-xs text-ink-muted">
          <MatrixLegend className="hidden md:flex" />
          {list.data && (
            <Pager
              meta={list.data.meta}
              onPage={(page) => onSearch({ page })}
            />
          )}
        </footer>
      </section>

      {(selected.size > 0 || grid.dirty) && (
        <FloatingBar>
          {selected.size > 0 && (
            <>
              <span className="flex items-center gap-2 font-semibold">
                <span className="num">{selected.size}</span> selected
                <button
                  type="button"
                  aria-label="Clear selection"
                  onClick={() => setSelected(new Set())}
                  className="rounded-md p-1 text-forest-ink hover:bg-white/10 hover:text-white"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </span>
              <span className="flex flex-wrap items-center gap-1">
                <RolePicker
                  label="Grant role"
                  roles={roleList}
                  onPick={(r) => stageForSelection(r.id, true)}
                />
                <RolePicker
                  label="Remove role"
                  roles={roleList}
                  onPick={(r) => stageForSelection(r.id, false)}
                />
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="sm" variant="ghost" className={BAR_BTN}>
                      <Factory className="h-4 w-4" /> Add plant
                      <ChevronDown className="h-3.5 w-3.5 opacity-70" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" side="top">
                    <DropdownMenuLabel>
                      Add a plant to everyone selected
                    </DropdownMenuLabel>
                    {(facilities.data ?? []).map((f) => (
                      <DropdownMenuItem
                        key={f.id}
                        onSelect={() =>
                          setBulk({
                            kind: "plant",
                            id: f.id,
                            label: `${f.plantCode} · ${f.name}`,
                          })
                        }
                      >
                        <span className="font-mono text-xs">{f.plantCode}</span>{" "}
                        {f.name}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button
                  size="sm"
                  variant="ghost"
                  className={BAR_BTN}
                  onClick={() => setBulk({ kind: "deactivate" })}
                >
                  <Power className="h-4 w-4" /> Deactivate
                </Button>
              </span>
            </>
          )}
          {selected.size > 0 && grid.dirty && (
            <span
              className="hidden h-6 w-px bg-white/15 sm:block"
              aria-hidden
            />
          )}
          {grid.dirty && (
            <ChangesControls
              count={grid.count}
              summary={plural(grid.diff.length, "person", "people")}
              onDiscard={grid.discard}
              onReview={() => {
                save.reset();
                setReview(true);
              }}
            />
          )}
        </FloatingBar>
      )}

      <ReviewDialog
        open={review}
        onOpenChange={setReview}
        title="Save role changes?"
        description="Each person re-authenticates on their next request, so a removed role stops working at once."
        confirmLabel={`Save ${plural(grid.count, "change", "changes")}`}
        saving={save.isPending}
        error={
          save.error ? errText(save.error, "Could not save the changes") : null
        }
        onConfirm={() => save.mutate()}
        groups={grid.diff.map((d) => {
          const u = known.get(Number(d.row));
          return {
            key: d.row,
            title: (
              <span className="flex items-center gap-2">
                <Avatar name={u?.fullName ?? d.row} seed={u?.email} size="sm" />
                {u?.fullName ?? `User #${d.row}`}
              </span>
            ),
            add: d.add.map((k) => roleName.get(k) ?? k),
            remove: d.remove.map((k) => roleName.get(k) ?? k),
          };
        })}
      />

      <ConfirmDialog
        open={bulk?.kind === "deactivate"}
        title={`Deactivate ${plural(selected.size, "person", "people")}?`}
        description="They are signed out and move to Inactive. Their records and history stay, and each can be reactivated later."
        confirmLabel="Deactivate"
        danger
        onClose={() => setBulk(null)}
        onConfirm={() =>
          runBulk("Deactivated", (u) => adminApi.setActive(u.id, false))
        }
      />
      <ConfirmDialog
        open={bulk?.kind === "plant"}
        title={`Add ${bulk?.kind === "plant" ? bulk.label : ""}?`}
        description={`Everyone selected keeps their current plants and gains this one. ${plural(selected.size, "person", "people")}.`}
        confirmLabel="Add plant"
        onClose={() => setBulk(null)}
        onConfirm={() => {
          if (bulk?.kind !== "plant") return;
          const plant = bulk.id;
          return runBulk("Plant added", (u) =>
            u.facilities.some((f) => f.id === plant)
              ? Promise.resolve()
              : adminApi.updateUser(u.id, {
                  fullName: u.fullName,
                  phone: normalizeMobile(u.phone ?? "") || null,
                  operatorCode: u.operatorCode,
                  roleIds: u.roles.map((r) => r.id),
                  facilityIds: [...u.facilities.map((f) => f.id), plant],
                  ...accessWindowPayload(accessWindowOf(u)),
                })
          );
        }}
      />

      <PersonSheet
        user={openUser}
        roles={roleList}
        isSelf={openUser?.id === selfId}
        matrixPending={
          openUser !== null &&
          grid.diff.some((d) => d.row === String(openUser.id))
        }
        onClose={() => setOpenId(null)}
      />

      <InviteSheet
        open={invite}
        onClose={() => setInvite(false)}
        roles={roleList}
      />
    </>
  );
}

const BAR_BTN = "text-white hover:bg-white/10 hover:text-white";

function RolePicker({
  label,
  roles,
  onPick,
}: {
  label: string;
  roles: AdminRole[];
  onPick: (r: AdminRole) => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="sm" variant="ghost" className={BAR_BTN}>
          {label === "Grant role" ? (
            <UserPlus className="h-4 w-4" />
          ) : (
            <UserMinus className="h-4 w-4" />
          )}
          {label}
          <ChevronDown className="h-3.5 w-3.5 opacity-70" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        side="top"
        className="max-h-80 overflow-y-auto"
      >
        <DropdownMenuLabel>
          {label === "Grant role"
            ? "Stage this role for everyone selected"
            : "Stage removing this role"}
        </DropdownMenuLabel>
        {roles.map((r) => (
          <DropdownMenuItem key={r.id} onSelect={() => onPick(r)}>
            {r.name}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function PersonHead({
  user,
  isSelf,
  selected,
  onSelect,
  onOpen,
}: {
  user: AdminUser;
  isSelf: boolean;
  selected: boolean;
  onSelect: (on: boolean) => void;
  onOpen: () => void;
}) {
  return (
    <div className="flex items-center gap-2.5">
      <Checkbox
        aria-label={`Select ${user.fullName}`}
        checked={selected}
        disabled={isSelf}
        onCheckedChange={(c) => onSelect(c === true)}
      />
      <Avatar name={user.fullName} seed={user.email} />
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-1.5">
          <span className="truncate text-sm font-bold text-ink">
            {user.fullName}
          </span>
          {user.operatorCode && (
            <span className="shrink-0 rounded border border-border px-1 font-mono text-2xs font-semibold leading-4 text-ink-secondary">
              {user.operatorCode}
            </span>
          )}
          {isSelf && (
            <span className="shrink-0 text-2xs font-bold uppercase tracking-wide text-brand-text">
              You
            </span>
          )}
        </div>
        <div className="flex min-w-0 items-center gap-1.5 text-xs text-ink-muted">
          <PlantCodes user={user} />
          <span aria-hidden>·</span>
          <span className="truncate">{user.email}</span>
        </div>
      </div>
      <UserStatusBadge user={user} quiet />
      {/* The person opens from this button, never from their name (user, 2026-10-01). */}
      <RowOpenButton variant="outline" onClick={onOpen} />
    </div>
  );
}

/** Phones get a list, not a grid: one person per row with their roles as chips. */
function PeopleList({
  users,
  onOpen,
}: {
  users: AdminUser[];
  onOpen: (u: AdminUser) => void;
}) {
  return (
    <ul className="divide-y divide-border md:hidden">
      {users.map((u) => (
        <li key={u.id}>
          <button
            type="button"
            onClick={() => onOpen(u)}
            className="flex w-full items-start gap-3 px-4 py-3 text-left active:bg-surface-sunken"
          >
            <Avatar name={u.fullName} seed={u.email} />
            <span className="min-w-0 flex-1 space-y-1">
              <span className="flex items-center justify-between gap-2">
                <span className="truncate text-sm font-bold text-ink">
                  {u.fullName}
                </span>
                <UserStatusBadge user={u} quiet />
              </span>
              <span className="block truncate text-xs text-ink-muted">
                {u.email}
              </span>
              <span className="flex flex-wrap items-center gap-1">
                {u.roles.length ? (
                  u.roles.map((r) => (
                    <Badge key={r.id} tone="manual" variant="pill">
                      {r.name}
                    </Badge>
                  ))
                ) : (
                  <span className="text-xs text-ink-muted">No role</span>
                )}
              </span>
              <span className="block text-xs">
                <PlantCodes user={u} />
              </span>
            </span>
            <ChevronRight
              className="mt-1 h-4 w-4 shrink-0 text-ink-muted"
              aria-hidden
            />
          </button>
        </li>
      ))}
    </ul>
  );
}

function Pager({
  meta,
  onPage,
}: {
  meta: { page: number; limit: number; total: number; pages: number };
  onPage: (page: number) => void;
}) {
  const from = meta.total === 0 ? 0 : (meta.page - 1) * meta.limit + 1;
  const to = Math.min(meta.page * meta.limit, meta.total);
  return (
    <div className="ml-auto flex items-center gap-2">
      <span className="num font-medium">
        {from}–{to} of {meta.total.toLocaleString("en-IN")}
      </span>
      <Button
        variant="secondary"
        size="icon-sm"
        aria-label="Previous page"
        disabled={meta.page <= 1}
        onClick={() => onPage(meta.page - 1)}
      >
        <ChevronLeft className="h-4 w-4" />
      </Button>
      <Button
        variant="secondary"
        size="icon-sm"
        aria-label="Next page"
        disabled={meta.page >= meta.pages}
        onClick={() => onPage(meta.page + 1)}
      >
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  );
}

function GridSkeleton() {
  return (
    <div aria-hidden className="divide-y divide-border">
      <div className="h-16 bg-surface-header" />
      {Array.from({ length: 6 }, (_, i) => (
        <div key={i} className="flex items-center gap-3 px-4 py-3">
          <Skeleton className="h-8 w-8 rounded-full" />
          <div className="w-64 space-y-1.5">
            <Skeleton className="h-3.5 w-40" />
            <Skeleton className="h-3 w-56" />
          </div>
          <div className="flex flex-1 gap-8 pl-8">
            {Array.from({ length: 8 }, (_, j) => (
              <Skeleton key={j} className="h-4 w-4 rounded-full" />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
